import { MessageType } from "/modules/components/notifier.js";
import { config } from "/modules/config.js";
import { systempb, userpb } from "/modules/rpc.js";
import { sessionStorageUtil } from "/modules/session-storage-util.js";

import { util } from "/modules/util.js";
import "/modules/vendor/@material/mwc-button.js";
import "/modules/components/dialog.js";
//import "/modules/vendor/@material/mwc-dialog.js";
import "/modules/vendor/@material/mwc-icon.js";
import tailwind from "/modules/tailwind.css.js";

// @ts-expect-error
import Cookies from "/modules/vendor/js-cookie.js";

import { html } from "/modules/vendor/lit.js";
import { BaseElement } from "/modules/components/base-element.js";
import { customElement, query, state } from "/modules/vendor/lit/decorators.js";
import "/modules/components/account/request-two-factor-auth-reset-action.js";
import "/modules/components/page/modal-portal.js";
import timedCache from "/modules/timed-cache.js";

declare global {
	interface HTMLElementTagNameMap {
		"komit-login-view": LoginView;
	}
}

@customElement("komit-login-view")
export class LoginView extends BaseElement {
	@state()
	private isOpen = false;

	@query("#loginForm")
	loginForm: HTMLFormElement | undefined;

	@query("#email")
	email: HTMLInputElement | undefined;

	@query("#password")
	password: HTMLInputElement | undefined;

	@query("#twoFactorAuthCode")
	twoFactorAuthCode: HTMLInputElement | undefined;

	@query("#resetPasswordEmail")
	resetPasswordEmail: HTMLInputElement | undefined;

	connectedCallback() {
		super.connectedCallback();

		if (
			Cookies.get("ExpirationTime") != undefined &&
			window.location.pathname == "/"
		) {
			this.redirect(null);
		}

		timedCache
			.cacheRequest(
				2000,
				"config.rpcClient.SystemSettingService.appStatus",
				systempb.AppStatusReq.create({}),
				config.rpcClient.SystemSettingService.appStatus.bind(
					config.rpcClient.SystemSettingService,
				),
			)
			.then((resp) => {
				const url = new URL(window.location.href);
				const email = url.searchParams.get("email");
				const password = url.searchParams.get("password");

				if (resp.isFirstLoginAlreadyHappened === false) {
					if (email && password && this.email && this.password) {
						this.email.value = email;
						this.password.value = password;
					}
				} else if (email && this.email) {
					this.email.value = email;
				}
			});
	}

	// necessary to make login work with some password managers
	// see https://github.com/komit-ag/komit-dev/issues/91 for details
	protected createRenderRoot() {
		return this;
	}

	private redirect(redirectPath: string | null) {
		if (sessionStorageUtil.isRestrictedToAuthService()) {
			window.location.href = "/settings/";
		} else {
			if (redirectPath) {
				window.location.href = redirectPath;
				return;
			}

			window.location.href = "/dashboard/";
		}
	}

	login(e: CustomEvent) {
		e.preventDefault();

		let req = userpb.LoginAuthReq.create({
			email: this.email?.value,
			password: this.password?.value,
			twoFactorAuthCode: this.twoFactorAuthCode?.value,
		});

		config.rpcClient.AuthService.login(req).then(async (resp) => {
			const externalPath = sessionStorageUtil.get<string>(
				// external path will be used for redirection after login
				// instead of default (currently /dashboard/)
				sessionStorageUtil.externalURLPathToRedirectAfterLoginKey,
			);

			// shouldn't be necessary because done on logout, just for safety for cases
			// when for some reason not triggered on logout
			// UPD: must stay here to remove externalURLPathToRedirectAfterLoginKey
			sessionStorage.clear();

			sessionStorageUtil.setIsRestrictedToAuthService(
				resp.isRestrictedToAuthService,
			);

			await util.setAccount(resp.account);

			// redirect to external path if present
			this.redirect(externalPath);
		});
	}

	requestNewPassword(e: CustomEvent) {
		e.preventDefault();

		let req = userpb.ResetPasswordAuthReq.create({
			email: this.resetPasswordEmail?.value,
		});

		config.rpcClient.AuthService.resetPassword(req).then(() => {
			this.closingDialog();

			util.notify(
				MessageType.Info,
				util.tr(
					"If the email address is registered, you will receive a new password by email.",
				),
			);
		});
	}

	openPasswordForgottenDialog() {
		if (this.resetPasswordEmail && this.email) {
			this.resetPasswordEmail.value = this.email.value;
		}
		this.isOpen = true;
	}

	closingDialog() {
		this.isOpen = false;
	}

	// TODO use <komit-form-dialog> instead of custom form for password reset
	// TODO use FormUtil.load() or better impl new tag for login form generation
	render() {
		return html`
			<style>
				${tailwind} .background-container {
					width: 100dvw;
					height: 100dvh;

					background-image: url(/img/login-view/curved-part.svg),
						url(/img/login-view/background-2.png);
					background-size: 80%, cover;
					background-repeat: no-repeat, no-repeat;
					background-position:
						top left,
						top center;
				}

				.background-container-mobile {
					display: none;
				}

				.login-form {
					width: 500px;
				}

				@media (max-width: 1500px) {
					.login-form {
						margin-left: -250px;
						width: 380px;
					}
				}

				@media (orientation: portrait) {
					.background-container {
						background-position:
							top left,
							top right 30%;
					}

					.login-form {
						margin-left: -250px;
						width: 380px;
					}
				}

				@media (max-width: 640px) and (orientation: portrait) {
					.login-form-container {
						justify-content: flex-end;
					}

					.login-form {
						margin-left: 0;
						width: 100%;
					}

					.background-container {
						background: none;
					}

					.background-container-mobile {
						display: block;
						width: 100%;
						height: fit-content;
						flex: 1;

						background-image: url(/img/login-view/curved-part.svg),
							url(/img/login-view/background-2.png);
						background-size: 70%, cover;
						background-repeat: no-repeat, no-repeat;
						background-position:
							top left,
							top right;
					}

					.logo {
						display: none;
					}

					.logo-2 {
						display: block;
					}
				}

				@media (max-width: 360px) {
					.logo-2 {
						height: 4rem;
					}

					.background-container-mobile {
						background-position:
							top left,
							top right 15%;
					}
				}

				@media (max-height: 700px) and (orientation: landscape) {
					.login-form {
						margin-top: 100px;
					}
				}

				@media (max-height: 600px) and (orientation: landscape) {
					.login-form-container {
						flex-direction: row;
						width: 100%;
						height: 100%;
					}

					.login-form {
						margin: 0;
						height: 100%;
						display: flex;
						width: fit-content;
						overflow-y: scroll;
					}

					.background-container {
						background: none;
					}

					.login-form-wrapper {
						border: none;
						box-shadow: none;
						border-radius: 0;
						height: fit-content;
						min-width: 370px;
					}

					.background-container-mobile {
						display: block;
						flex: 1;
						height: 100%;

						background-image: url(/img/login-view/curved-part.svg),
							url(/img/login-view/background-2.png);
						background-size: 70%, cover;
						background-repeat: no-repeat, no-repeat;
						background-position:
							top left,
							top right;
					}

					.logo {
						display: none;
					}

					.logo-2 {
						display: block;
					}
				}

				@media (max-width: 720px) and (orientation: landscape) {
					.logo-2 {
						height: 4rem;
					}

					.background-container-mobile {
						background-position:
							top left,
							top right 15%;
					}
				}

				@media (max-width: 630px) and (orientation: landscape) {
					.logo-2 {
						height: 3rem;
					}
				}
			</style>
			<!-- 
			45 or 225 deg 
			linear-gradient(45deg, #D2F5FB, #FBD0F1);
			
			linear-gradient(45deg, #D2F5FB, #D4E7FB)
			
			Matisse 100 minus 60 s(aturation)
				#F0F9FB

			Matisse 100 minus 70 s(aturation)
				#F5F9FB
			-->

			<div class="background-container">
				<img
					class="logo absolute h-36 pointer-events-none"
					src="/img/login-view/komit-logo-white.svg"
					alt="Logo"
				/>
				<div
					class="login-form-container flex flex-col items-center justify-center w-full h-full"
				>
					<div class="background-container-mobile">
						<img
							class="logo-2 h-20 pointer-events-none"
							src="/img/login-view/komit-logo-white.svg"
							alt="Logo"
						/>
					</div>
					<div class="login-form">
						<div
							class="login-form-wrapper bg-white py-6 px-4 shadow-lg sm:rounded-3xl sm:border sm:border-solid sm:border-gray-300 sm:px-6"
						>
							<form
								id="loginForm"
								@submit="${this.login}"
								class="space-y-5"
								action="#"
								method="POST"
							>
								<div>
									<label
										for="email"
										class="mb-1 block text-sm font-semibold leading-5 text-gray-700"
									>
										${util.tr("Email address")}
									</label>
									<input
										id="email"
										name="email"
										type="email"
										autocomplete="email"
										required
										autofocus
										class="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
									/>
								</div>

								<div>
									<label
										for="password"
										class="mb-1 block text-sm font-semibold leading-5 text-gray-700"
									>
										${util.tr("Password")}
									</label>
									<input
										id="password"
										name="password"
										type="password"
										autocomplete="current-password"
										required
										class="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
									/>
								</div>

								<div>
									<div class="flex justify-between">
										<label
											for="twoFactorAuthCode"
											class="mb-1 block text-sm font-semibold text-gray-700"
										>
											${util.tr("Two-factor auth code")}
										</label>
										<span class="text-sm text-gray-500"
											>Optional</span
										>
									</div>
									<input
										id="twoFactorAuthCode"
										name="twoFactorAuthCode"
										type="text"
										autocomplete="off"
										class="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
									/>
								</div>

								<div class="pt-1 text-left">
									<button
										type="submit"
										class="inline-flex items-center rounded-3xl border border-transparent bg-cerise-red-600 px-4 py-2 text-sm font-medium uppercase tracking-wider text-white shadow-sm hover:bg-cerise-red-600 focus:outline-none focus:ring-2 focus:ring-cerise-red-600 focus:ring-offset-2"
									>
										${util.tr("Sign in")}
										<mwc-icon
											class="ml-2 text-sm"
											style="font-size: 1.15rem"
										>
											login
										</mwc-icon>
									</button>
								</div>
							</form>
							<div class="mt-3 flex flex-row-reverse">
								<div class="text-sm">
									<a
										@click="${this
											.openPasswordForgottenDialog}"
										href="#"
										class="font-medium text-teal-500 hover:text-cerise-red-600"
									>
										${util.tr("Forgot your password?")}
									</a>
								</div>
							</div>
							<div class="mt-3 flex flex-row-reverse">
								<div class="text-sm">
									<komit-request-two-factor-auth-reset-action>
										<a
											href="#"
											class="font-medium text-teal-500 hover:text-cerise-red-600"
										>
											${util.tr(
												"Reset two-factor authentication",
											)}
										</a>
									</komit-request-two-factor-auth-reset-action>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<komit-dialog
				@closing=${this.closingDialog}
				?open=${this.isOpen}
				id="dialog"
				heading="${util.tr("Forgot your password?")}"
				narrow
			>
				<div>
					<label
						for="email"
						class="mb-1 block text-sm font-semibold leading-5 text-gray-700"
					>
						${util.tr("Email address")}
					</label>
					<input
						id="resetPasswordEmail"
						name="resetPasswordEmail"
						type="email"
						autocomplete="email"
						required
						class="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
					/>
				</div>
				<mwc-button
					@click=${this.requestNewPassword}
					raised
					slot="primaryAction"
				>
					${util.tr("Request new password")}
				</mwc-button>
				<mwc-button dialogAction="close" slot="secondaryAction">
					${util.tr("Close")}
				</mwc-button>
			</komit-dialog>
		`;
	}
}
